import { Component, OnInit, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { DataService, FormInputComponent, InputComponentConfig, ModalService, SharedModule } from '@vendure/admin-ui/core';
import { GET_PARCEL_PROFILES  } from './gql/parcel-profile.graphql';
import { ParcelProfile } from '../../common/ui/generated';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { ParcelProfileDialogComponent } from './parcel-profile-dialog.component';

interface GetParcelProfilesQuery {
    parcelProfiles: {
        items: ParcelProfile[];
        totalItems: number;
    }
}

interface GetDefaultParcelProfileQuery {
    parcelDefaultProfile: ParcelProfile;
}

@Component({
    selector: 'parcel-profile-selector',
    template: `
    <clr-dropdown [clrCloseMenuOnItemClick]="true" style="width: 100%">
      <button clrDropdownTrigger class="btn btn-md btn-outline full-width-trigger">
        <span>{{ formControl.value?.name ?? 'Select a profile' }}</span>
        <clr-icon shape="angle" dir="down"></clr-icon> </button>

      <clr-dropdown-menu clrPosition="bottom-left">
        <div class="menu-scroll">
          <button clrDropdownItem (click)="formControl.setValue(null)">
            — Select a profile —
          </button>

          <button *ngFor="let profile of parcelProfiles"
            clrDropdownItem
            [class.active]="byId(profile, formControl.value)"
            (click)="formControl.setValue(profile)">
            {{ profile.name }}
          </button>

          <button clrDropdownItem class="text-success" (click)="openCreateProfileModal()">
            ➕ Add new profile
          </button>
        </div>
      </clr-dropdown-menu>
    </clr-dropdown>
    `,
    standalone: true,
    styles: [`
      .menu-scroll {
        max-height: 120px;
        overflow-y: auto;
        overscroll-behavior: contain;
      }
      .full-width-trigger {
        display: flex;
        width: 100%;
        justify-content: space-between;
        align-items: center;
        text-align: left;
      }
      .full-width-trigger span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `],
    imports: [SharedModule],
})
export class ParcelProfileSelectorComponent
    implements OnInit, AfterViewInit, FormInputComponent<InputComponentConfig>
{
    readonly: boolean;
    formControl: UntypedFormControl;
    config: InputComponentConfig;
    parentPage: string;
    parcelProfiles: ParcelProfile[] = [];
    readonly NEW_MARKER = '__new__';

    constructor(
      private dataService: DataService,
      private router: Router,
      private modalService: ModalService,
      private cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
      if (this.formControl?.value && typeof this.formControl.value === 'string') {
        this.formControl.setValue({ id: this.formControl.value } as ParcelProfile, { emitEvent: false });
      }

      this.loadParcelProfiles();
    }

    ngAfterViewInit() {
      this.formControl?.valueChanges.subscribe(v => {
        if (v === this.NEW_MARKER) {
          this.openCreateProfileModal();
        }
      });
    }

    byId = (a: ParcelProfile | string | null, b: ParcelProfile | string | null): boolean =>
      typeof a === 'object' && typeof b === 'object'
        ? a?.id === b?.id
        : a === b;

    private loadParcelProfiles() {
        this.dataService.query<GetParcelProfilesQuery>(GET_PARCEL_PROFILES)
            .mapSingle(data => data.parcelProfiles)
            .subscribe({
                next: (profiles) => {
                    this.parcelProfiles = profiles.items || [];
                    this.cdr.detectChanges();
                },
                error: (error) => console.error('Error loading parcel profiles:', error)
            });
    }

    openCreateProfileModal() {
        const oldValue = this.formControl.value;
        this.modalService
            .fromComponent(ParcelProfileDialogComponent, {
                locals: {
                  mode: 'create',
                },
                size: 'lg',
            })
            .subscribe(result => {
                if (result) {
                    this.parcelProfiles = [...this.parcelProfiles, result];
                    this.formControl.setValue(result);
                } else {
                  if(oldValue === this.NEW_MARKER) {
                    this.formControl.setValue(null);
                  } else {
                    this.formControl.setValue(oldValue);
                  }
                }
                this.cdr.detectChanges();
            });
    }

    private findParam(router: Router, key: string): string | null {
        let node: ActivatedRouteSnapshot | null = router.routerState.snapshot.root;
        while (node) {
            if (node.paramMap.has(key)) {
                return node.paramMap.get(key);
            }
            node = node.firstChild ?? null;
        }
        return null;
    }
}
